import axios from 'axios';

const API_BASE_URL = '/api';

// Create axios instance with auth
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and tenant site ID
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Send active site ID for multi-tenant scoping
  const activeSiteId = localStorage.getItem('activeSiteId');
  if (activeSiteId) {
    config.headers['x-site-id'] = activeSiteId;
  }
  return config;
});

// Add response interceptor to handle module access errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response.data?.module) {
      // This is a module access error
      const moduleError = error.response.data;
      
      // Store the error for UI components to handle
      localStorage.setItem('moduleAccessError', JSON.stringify(moduleError));
      
      // Redirect to the module access error page
      window.location.href = '/module-access-error';
      
      // Prevent the error from propagating
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// Types
export interface SitePricing {
  _id: string;
  siteId: string;
  basePrice: {
    amount: number;
    currency: string;
    billingPeriod: string;
  };
  premiumFeaturePricing: {
    financialReports: {
      enabled: boolean;
      price: number;
    };
    customerPortal: {
      enabled: boolean;
      price: number;
    };
    delivery: {
      enabled: boolean;
      price: number;
    };
  };
  discounts: {
    percentage: number;
    reason: string;
    validUntil?: string;
  };
  customPricing: {
    enabled: boolean;
    reason: string;
  };
  billingInfo: {
    companyName: string;
    billingAddress: {
      street: string;
      city: string;
      postcode: string;
      country: string;
    };
    contactEmail: string;
    contactPhone: string;
    vatNumber: string;
  };
  billingSettings: {
    dayOfMonth: number;
    nextBillingDate: string;
    autoRenew: boolean;
    paymentMethod: string;
  };
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface PricingBreakdown {
  basePrice: number;
  premiumFeatures: Array<{
    name: string;
    price: number;
  }>;
  discounts: Array<{
    percentage: number;
    amount: number;
    reason: string;
  }>;
  subtotal: number;
  total: number;
}

export interface SitePricingResponse {
  pricing: SitePricing;
  monthlyTotal: number;
  pricingBreakdown: PricingBreakdown;
}

// API Functions
export const sitePricingAPI = {
  // Get pricing for a site
  getSitePricing: (siteId: string) => 
    api.get<SitePricing>(`/site-pricing/sites/${siteId}/pricing`),

  // Update pricing for a site (super admin only)
  updateSitePricing: (siteId: string, pricingData: Partial<SitePricing>) => 
    api.put<SitePricingResponse>(`/site-pricing/sites/${siteId}/pricing`, pricingData),

  // Get all site pricings (super admin only)
  getAllSitePricings: () => 
    api.get<SitePricingResponse[]>(`/site-pricing/pricing`),

  // Update premium feature pricing (super admin only)
  updatePremiumFeaturePricing: (siteId: string, feature: string, enabled: boolean, price: number) => 
    api.put<SitePricingResponse>(`/site-pricing/sites/${siteId}/pricing/premium-features`, {
      feature,
      enabled,
      price
    }),

  // Apply discount (super admin only)
  applyDiscount: (siteId: string, percentage: number, reason: string, validUntil?: string) => 
    api.post<SitePricingResponse>(`/site-pricing/sites/${siteId}/pricing/discount`, {
      percentage,
      reason,
      validUntil
    }),

  // Remove discount (super admin only)
  removeDiscount: (siteId: string) => 
    api.delete<SitePricingResponse>(`/site-pricing/sites/${siteId}/pricing/discount`),

  // Get pricing summary for dashboard
  getPricingSummary: (siteId: string) => 
    api.get<{
      monthlyTotal: number;
      pricingBreakdown: PricingBreakdown;
      nextBillingDate: string;
      billingPeriod: string;
      currency: string;
    }>(`/site-pricing/sites/${siteId}/pricing/summary`)
};
